import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isNativeApp } from './api';

/** Native side lives in android/app/src/main/java/app/sensedaily/pos/SdDevicesPlugin.java. */
export interface PairedDevice { name: string; address: string; kind: 'printer' | 'phone' | 'computer' | 'audio' | 'other' }
export interface NativeInfo { bluetooth: boolean; bluetoothOn: boolean; print: boolean; speech: boolean; sdk: number; model: string; connected: string[]; battery?: number; charging?: boolean; output?: string }
/** A payment the phone heard from a UPI app or bank SMS (PaymentListener.java). */
export interface ReceivedPayment { id: string; amount: number; payer: string; app: string; ts: number }
interface SdDevicesPlugin {
  info(): Promise<NativeInfo>;
  paired(): Promise<{ devices: PairedDevice[] }>;
  connect(o: { id: string; address: string }): Promise<{ id: string; name: string | null }>;
  write(o: { id: string; data: string }): Promise<void>;
  disconnect(o: { id: string }): Promise<void>;
  printHtml(o: { html: string; name: string }): Promise<void>;
  listen(o: { lang: string }): Promise<{ text: string }>;
  stopListening(): Promise<void>;
  speak(o: { text: string; lang: string; rate: number; volume: number }): Promise<void>;
  voiceInfo(): Promise<{ ready: boolean; engine: string; languages: Record<string, boolean> }>;
  paymentAlerts(): Promise<{ granted: boolean }>;
  openPaymentAlertSettings(): Promise<void>;
  setSoundbox(o: { enabled: boolean; listen: boolean; lang: string; rate: number; volume: number }): Promise<void>;
  recentPayments(): Promise<{ payments: ReceivedPayment[] }>;
  addListener(e: 'payment', fn: (d: ReceivedPayment) => void): Promise<PluginListenerHandle>;
  addListener(e: 'data', fn: (d: { id: string; text: string }) => void): Promise<PluginListenerHandle>;
  addListener(e: 'disconnected', fn: (d: { id: string }) => void): Promise<PluginListenerHandle>;
  addListener(e: 'speech', fn: (d: { state: string; text?: string }) => void): Promise<PluginListenerHandle>;
}

/** Only usable inside the Android app; check `isNativeApp()` first. */
export const SdDevices = registerPlugin<SdDevicesPlugin>('SdDevices');
export { isNativeApp };

export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

/** Native plugin errors carry the readable message the Java side wrote. */
export const nativeMessage = (e: unknown) => (e as { message?: string })?.message ?? String(e);
